import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  ListItemText,
  MenuItem,
  Select,
  SxProps,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";

import type {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
} from "../form-field";

export const DROPDOWN_FORM_FIELD_TYPE = "dropdown" as const;

export type DropdownFormFieldDefinition = BaseFormFieldDefinition<
  typeof DROPDOWN_FORM_FIELD_TYPE
> & {
  type: typeof DROPDOWN_FORM_FIELD_TYPE;
  items: {
    value: string;
    label: string;
    color?: string;
    description?: string;
  }[];
  multiSelect?: boolean;
  menuItemsSx?: SxProps;
  placeholder?: string;
};

export function DropdownFormField({
  disabled,
  errorMessage,
  items,
  multiSelect,
  name,
  required,
  sx,
  menuItemsSx,
  placeholder,
}: DropdownFormFieldDefinition & AdditionalFormFieldProps) {
  const { control } = useFormContext();

  const renderSelectedValue = (selected: string | string[]) => {
    const displayLimit = 3;

    if (multiSelect) {
      const selectedValues = selected as string[];
      if (selectedValues.length === 0 && placeholder) return placeholder;
      const selectedOptions = selectedValues
        .map((value) => items.find((i) => i.value === value))
        .filter(Boolean) as NonNullable<(typeof items)[number]>[];

      const displayOptions = selectedOptions.slice(0, displayLimit);
      const moreCount = selectedOptions.length - displayLimit;

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {displayOptions.map((option, idx) => (
            <Box
              key={option.value}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {option.color && (
                <Box
                  sx={{
                    bgcolor: option.color,
                    width: 16,
                    height: 16,
                    borderRadius: "100%",
                    border: "1px solid",
                    borderColor: "gray",
                    marginRight: 0.5,
                  }}
                />
              )}
              <span>{option.label}</span>
              {idx < displayOptions.length - 1 && <span>,</span>}
            </Box>
          ))}
          {moreCount > 0 && <span>+ {moreCount} more</span>}
        </Box>
      );
    }

    const selectedValue = selected as string;
    if (!selectedValue && placeholder) return placeholder;
    const selectedOption = items.find((i) => i.value === selectedValue);
    if (!selectedOption) return selectedValue;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {selectedOption.color && (
          <Box
            sx={{
              bgcolor: selectedOption.color,
              width: 16,
              height: 16,
              borderRadius: "100%",
              border: "1px solid",
              borderColor: "gray",
              marginRight: 0.5,
            }}
          />
        )}
        <span>{selectedOption.label}</span>
      </Box>
    );
  };

  return (
    <FormControl fullWidth error={!!errorMessage}>
      <Controller
        control={control}
        name={name}
        rules={{ required }}
        render={({ field }) => (
          <Select
            {...field}
            sx={{
              borderRadius: "6px",
              ...(multiSelect
                ? {
                    "& .MuiSelect-select": {
                      minHeight: "45px !important",
                      maxHeight: 100,
                      overflowY: "auto",
                      boxSizing: "border-box",
                    },
                  }
                : { height: "45px" }),
              ...sx,
            }}
            disabled={disabled}
            error={!!errorMessage}
            slotProps={{
              input: {
                style: {
                  borderRadius: "6px",
                  ...(multiSelect ? {} : { height: "45px" }),
                },
              },
            }}
            multiple={multiSelect}
            displayEmpty={Boolean(placeholder)}
            renderValue={renderSelectedValue}
            value={field.value ?? (multiSelect ? [] : "")}
          >
            {items.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {multiSelect && (
                  <Checkbox
                    checked={
                      !!(field.value as string[])?.includes(option.value)
                    }
                  />
                )}
                {option.color && (
                  <Box
                    sx={{
                      bgcolor: option.color,
                      width: 16,
                      height: 16,
                      borderRadius: "100%",
                      marginRight: 1,
                      border: "1px solid",
                      borderColor: "gray",
                    }}
                  />
                )}
                <ListItemText
                  primary={option.label}
                  secondary={option.description}
                  slotProps={{
                    primary: { sx: menuItemsSx },
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        )}
      />
      {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
}
